import moment from "moment";
import { API_URL } from "../config/config"
import { downloadRequest, getRequest, patchRequest, postRequest } from "../utils/axiosUtils"
import { Country, State } from 'country-state-city';
import { formatStoredPhone, getFilterQuery, getStatusLabel } from "../utils/helperFunction";

export const fetchUniversitiesNames = async (setUniversities) => {
  try {
    const response = await getRequest({
      url: `${API_URL}/university/names`,
    })

    if (response.status >= 200 && response.status < 300 && response.data) {
      const options = response.data.map(uni => ({
        value: uni._id,
        label: uni.universityName
      }))
      setUniversities(options)
    } else {
      setUniversities([])
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
    setUniversities([])
    console.error(error)
  }
}

export const saveUniversity = async (universityData) => {
  if (!universityData) {
    throw new Error('University data is required.')
  }

  const payload = {}
  universityData.forEach(field => {
    if (
      field.type === 'select' &&
      typeof field.value === 'object' &&
      field.value !== null
    ) {
      payload[field.id] = field.value.value
    } else {
      payload[field.id] = field.value
    }

    if(field.id=="awardsandRecognitions"){
      payload[field.id] = field.value?.filter(ele=> ele?.fileUrl || ele?.body || ele?.title)
    }
  })

  try {
    const response = await postRequest({
      url: `${API_URL}/university/save`,
      data: payload,
      headers: {
        'Content-Type': 'application/json'
      },
    })

    if (response?.data?.success === false) {
      const customError = new Error(response.data.message || 'Validation failed')
      customError.fieldErrors = response.data.errors || {}
      throw customError
    }

    return response.data
  } catch (err) {
    const responseData = err?.response?.data

    if (responseData) {
      const customError = new Error(responseData.message || 'University saving failed')
      customError.fieldErrors = responseData.errors || {}
      throw customError
    }

    throw new Error(err.message || 'Unknown error occurred')
  }
}

export const fetchUniversity = async (universityId, fieldTemplate = [],editMode) => {
  if (!universityId) throw new Error('University ID is required');

  try {
    const response = await getRequest({ url: `${API_URL}/university/${universityId}` });
    const university = response?.data?.university;

    if (!response?.data?.success || !university) {
      const error = new Error(response?.data?.message || 'University not found');
      error.fieldErrors = response?.data?.errors || {};
      throw error;
    }

    const isDraft = editMode || ['draft', 'rework'].includes(university.status);

    const allCountries = Country.getAllCountries();
    const normalizeCountry = (raw) => {
      if (!raw) return '';
      const s = String(raw).trim();
      const byCode = allCountries.find(c => c.isoCode.toLowerCase() === s.toLowerCase());
      if (byCode) return byCode.name;
      const byName = allCountries.find(c => c.name.toLowerCase() === s.toLowerCase());
      return byName ? byName.name : s;
    };

    const countryName = normalizeCountry(university.country);
    const countryObj = allCountries.find(c => c.name === countryName) || null;

    const normalizeState = (raw) => {
      if (!raw) return '';
      const s = String(raw).trim();
      if (!countryObj) return s;
      const states = State.getStatesOfCountry(countryObj.isoCode);
      const byCode = states.find(st => st.isoCode.toLowerCase() === s.toLowerCase());
      if (byCode) return byCode.name;
      const byName = states.find(st => st.name.toLowerCase() === s.toLowerCase());
      return byName ? byName.name : s;
    };

    const stateName = normalizeState(university.state);
    const cityName  = typeof university.city === 'string' ? university.city : '';
    const populatedFields = fieldTemplate.map((field) => {
      const rawValue = university[field.id];
      if (field.id === 'country') {
        return { ...field, value: countryName, readOnly: !isDraft };
      }
      if (field.id === 'state') {
        return { ...field, value: stateName, readOnly: !isDraft };
      }
      if (field.id === 'city') {
        return { ...field, value: cityName, readOnly: !isDraft };
      }
      if (field.id === 'uploadRelevantDocument' && Array.isArray(rawValue)) {
        const documents = rawValue.map(doc => ({
          title: doc.title || '',
          file: null,
          uploading: false,
          url: doc.url || ''
        }));
        return { ...field, value: documents, readOnly: !isDraft };
      }
      if (field.type === 'select' && rawValue) {
        if (typeof rawValue === 'object') {
          return {
            ...field,
            value: {
              label: rawValue.label || rawValue.name || rawValue.value || '',
              value: rawValue._id || rawValue.value || '',
            },
            readOnly: !isDraft
          };
        } else {
          return {
            ...field,
            value: { label: String(rawValue), value: rawValue },
            readOnly: !isDraft
          };
        }
      }
      return {
        ...field,
        value: rawValue !== undefined ? rawValue : '',
        readOnly: !isDraft
      };
    });

    return populatedFields;
  } catch (err) {
    const responseData = err?.response?.data;
    if (responseData) {
      const customError = new Error(responseData.message || 'University fetch failed');
      customError.fieldErrors = responseData.errors || {};
      throw customError;
    }
    throw new Error(err.message || 'Unknown error occurred while fetching university');
  }
};

export const updateUniversityStatus = async (id, universityStatus, reason = '') => {
  if (!id || !universityStatus) {
    throw new Error('University ID and status are required')
  }

  const response = await patchRequest({
    url: `${API_URL}/university/status/${id}`,
    data: { status: universityStatus, reason },
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (response?.data?.success === false) {
    throw new Error(response.data.message || 'Status update failed')
  }

  return response.data
}

export const fetchUniversityList = async (
  setRows,
  filterData = {},
  userId = true,
  isReviewer = false,
  setRowCount
) => {
  try {
    const modifyFilterData = {...filterData,...(!userId ? {isReviewer:isReviewer ? 'true' : 'false'}:{})}
    const query = getFilterQuery(modifyFilterData)
    const url = userId
      ? `${API_URL}/university/user${query}`
      : `${API_URL}/university/all${query}`;

    const response = await getRequest({
      url,
      // data: {
      //   ...filterData,
      //   isReviewer: isReviewer ? 'true' : 'false'
      // },
    });

    if (response.status >= 200 && response.status < 300 && response.data) {
      const pagination = response.data?.pagination
      const options = (response.data.universities || []).map((university, idx) => ({
        sNo: (pagination?.page-1)*pagination.limit + (idx+1),
        id: university._id,
        universityId: university.universityId || '',
        name: university.universityName || '',
        logo: university.universityLogo || '',
        tag: university.tag || 'uncategorized',
        enrollment: university.enrollmentWindow || '',
        status: (university.status || '').charAt(0).toUpperCase() + (university.status || '').slice(1),
        completion: university.completionPercentage || '',
        createdDate: university.createdAt ? new Date(university.createdAt).toLocaleDateString() : '',
        lastUpdate: university.updatedAt ? new Date(university.updatedAt).toLocaleDateString() : '',
        createdBy: university.createdBy || '',
        reviewedBy: university.reviewedBy || '',
        comments: Array.isArray(university.notes) && university.notes.length > 0
          ? university.notes.join(', ')
          : '',
        contactEmail: university.contactEmail || '',
        phone: university.telephoneNumber || '',
        studentHelpline: university.studentHelpline || '',
        aliasShortName: university.shortName || '',
        website: university.websiteLink || '',
        foundedYear: university.yearEstablished || '',
        type: university.institutionType || '',
        location: [
          university.city || '',
          university.state || '',
          university.country || ''
        ].filter(Boolean).join(', '),
        brochure: university.uploadBrochure,
        specializationCount: university?.specializationCount || 0,
        courseCount: university?.courseCount || 0,
        isFooter: university?.isFooter || false,
        isHomeVisible: university?.isHomeVisible || false
      }));

      setRows(options);
      setRowCount && setRowCount(response.data?.pagination?.total || 0)
    } else {
      setRows([]);
      throw new Error(`Unexpected response code: ${response.status}`);
    }
  } catch (error) {
    setRows([]);
    console.error(error);
  }
};

export const fetchUniversityDashboardStats = async (setStatusList) => {
  try {
    const response = await getRequest({
      url: `${API_URL}/university/dashboard-stats`
    })

    if (response.status >= 200 && response.status < 300 && response.data?.data) {
      const stats = response.data.data

      const updatedList = [
        { label: 'All University', count: stats.total || 0 },
        { label: 'Live University', count: stats.live || 0 },
        { label: 'Pending Approval', count: stats.pending || 0 },
        { label: 'Archive University', count: stats.archived || 0 },
        { label: 'Deactivated University', count: stats.deactivated || 0 },
        { label: 'Draft', count: stats.draft || 0 }
      ]

      setStatusList(updatedList)
    } else {
      console.warn(`Unexpected response: ${response.status}`)
      setStatusList([])
    }
  } catch (error) {
    console.error('Failed to fetch university dashboard stats:', error)
    setStatusList([])
  }
}

export const updateEnrollment = async ({ id, status, type }) => {
  if (!id || !status || !type) {
    throw new Error('Missing id, status, or type for tag update')
  }
  try {
    const response = await patchRequest({
      url: `${API_URL}/university/enrollment`,
      data: { id, status, type },
      headers: {
        'Content-Type': 'application/json'
      },
    })
    if (response?.data?.success === false) {
      const customError = new Error(response.data.message || 'Tag update failed')
      customError.fieldErrors = response.data.errors || {}
      throw customError
    }
    return response.data
  } catch (err) {
    const responseData = err?.response?.data
    if (responseData) {
      const customError = new Error(responseData.message || 'Tag update failed')
      customError.fieldErrors = responseData.errors || {}
      throw customError
    }
    throw new Error(err.message || 'Unknown error occurred')
  }
}

export const fetchUniversityRatingList = async ({
  setRows,
  filterData = {},
  setRowCount
}) => {
  try {
    const query = getFilterQuery(filterData)
    const url = `${API_URL}/university/review/getAllUniversityReview${query}`


    const response = await getRequest({url });

    if (response.status >= 200 && response.status < 300 && response.data) {
      const pagination = response.data?.pagination
      const options = (response.data.data || []).map((item, idx) => ({
        sNo: (pagination?.page-1)*pagination.limit + (idx+1),
        id: item._id,
        rating: item.rate || '',
        review: item.review || '',
        universityName: item.universityId?.universityName || '',
        studentDetails: item.userId?.fullName || '',
        emailID: item.userId?.email || '',
        phoneNumber: item.userId?.phone ? formatStoredPhone(item.userId?.phone) : '-', 
        status:item?.status ||  "Pending",
        submitted:item.ts ? moment(item.ts).format("DD/MM/YYYY") : '',

      }));

      setRows(options);
      setRowCount && setRowCount(response.data?.pagination?.total || 0)
    } else {
      setRows([]);
      throw new Error(`Unexpected response code: ${response.status}`);
    }
  } catch (error) {
    setRows([]);
    console.error(error);
      throw new Error(error?.response?.data?.message || `Failed to fetch details`);
  }
};

export const updateUniversityRatingStatus = async (id, status) => {
  try {
    if (!id || !status) {
      throw new Error('Rating ID and status are required')
    }
  
    const response = await patchRequest({
      url: `${API_URL}/university/review/updateUniversityReviewStatus`,
      data: { status,id},
    })
  
    if (response?.data?.success === false) {
      throw new Error(response.data.message || 'Status update failed')
    }
  
    return response.data
    
  } catch (error) {
      throw new Error(error?.response?.data?.message || `Failed to update details`);
  }
}

export const fetchUniversityCourseWithSpecializationList = async ({
  id,
  setDetails,
  setLoading
}) => {
  let details = []
  try {
    setLoading(true)
    const url = `${API_URL}/university/courseWithSpecializationList/${id}`
    const response = await getRequest({ url });

    if (response.status >= 200 && response.status < 300 && response.data) {
      const data = response.data.data || {}
      details = {
        ...data
      }

    } else {
      throw new Error(`Unexpected response code: ${response.status}`);
    }
  } catch (error) {
    console.error(error);
    throw new Error(error?.response?.data?.message || `Failed to fetch details`);
  } finally {
    setDetails(details)
    setLoading(false)

  }
};